import { Table, Text } from '@mantine/core';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  GpSurfacesMapSamplerCoverageRow,
  GpSurfacesMapViewMode,
} from '../../../lib/types/gp-surfaces-map';
import {
  formatHaUnitFr,
  formatPercentUnitFr,
} from '../../../lib/utils/gp-surfaces-format';

import { SamplerCoverageMetricText } from './sampler-coverage-metric-text';
import { SamplerCoverageMetricsCells } from './sampler-coverage-metrics-cells';
import { SamplerCoverageMetricsHead } from './sampler-coverage-metrics-head';

type GpSurfacesMapSamplerCoverageTableProps = {
  rows: GpSurfacesMapSamplerCoverageRow[];
  selectedDepartmentCode: string;
  viewMode: GpSurfacesMapViewMode;
};

export const GpSurfacesMapSamplerCoverageTable: FC<
  GpSurfacesMapSamplerCoverageTableProps
> = ({ rows, selectedDepartmentCode, viewMode }) => {
  const { t } = useTranslation();
  const withProjection = viewMode === 'with_projection';

  const visibleRows = useMemo(
    () => rows.filter((row) => row.department_code === selectedDepartmentCode),
    [rows, selectedDepartmentCode],
  );

  if (visibleRows.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        {t('surfacesMap.samplerCoverage.empty')}
      </Text>
    );
  }

  return (
    <Table
      highlightOnHover
      horizontalSpacing="xs"
      striped
      style={{ tableLayout: 'fixed', width: '100%' }}
      verticalSpacing="xs"
      withTableBorder
    >
        <Table.Thead>
          <SamplerCoverageMetricsHead
            leadingHeaders={[
              {
                key: 'sampler',
                label: t('surfacesMap.samplerCoverage.columns.sampler'),
              },
              {
                key: 'share',
                label: t('surfacesMap.samplerCoverage.columns.share'),
                ta: 'right',
              },
              {
                key: 'area',
                label: t('surfacesMap.samplerCoverage.columns.area'),
                ta: 'right',
              },
            ]}
          />
        </Table.Thead>
        <Table.Tbody>
          {visibleRows.map((row) => (
            <Table.Tr
              key={`${row.department_code}-${row.sampler_organization_id}`}
            >
              <Table.Td>
                <Text size="sm">{row.sampler_organization_name}</Text>
              </Table.Td>
              <Table.Td ta="right">
                <SamplerCoverageMetricText
                  value={formatPercentUnitFr(row.share_percent)}
                />
              </Table.Td>
              <Table.Td ta="right">
                <SamplerCoverageMetricText
                  value={formatHaUnitFr(
                    withProjection ? row.combined_area : row.actual_area,
                  )}
                />
              </Table.Td>
              <SamplerCoverageMetricsCells metrics={row} />
            </Table.Tr>
          ))}
        </Table.Tbody>
    </Table>
  );
};
